import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import { useEthersContext } from "@/providers/EthersProvider"

// Expected chain ID from environment
const DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true'
const DEV_CHAIN_ID = 1337
const PROD_CHAIN_ID = 56
const EXPECTED_CHAIN_ID = DEV_MODE ? DEV_CHAIN_ID : PROD_CHAIN_ID

// Pages that don't require network enforcement
const EXCLUDED_PAGES = ['/', '/bridge', '/stats', '/docs', '/static', '/terms', '/privacy']

export function NetworkGuard() {
  const location = useLocation()
  const { isConnected, chainId } = useEthersContext()
  const hasRequestedSwitch = useRef(false)

  useEffect(() => {
    // Reset switch request flag when chain changes
    hasRequestedSwitch.current = false
  }, [chainId])

  useEffect(() => {
    // Don't request on excluded pages (multi-network or informational pages)
    if (EXCLUDED_PAGES.some(page => location.pathname === page || (page !== '/' && location.pathname.startsWith(page)))) {
      return
    }

    // Don't request if not connected or already on correct network
    if (!isConnected || !chainId || chainId === EXPECTED_CHAIN_ID) {
      return
    }

    // Don't request if we already requested this session
    if (hasRequestedSwitch.current) {
      return
    }

    // Request network switch
    const requestSwitch = async () => {
      if (!window.ethereum) return

      hasRequestedSwitch.current = true

      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: `0x${EXPECTED_CHAIN_ID.toString(16)}` }],
        })
      } catch (error: unknown) {
        const err = error as { code?: number }
        // Chain not added to wallet, try to add it
        if (err.code === 4902) {
          try {
            const networkParams = DEV_MODE
              ? {
                  chainId: `0x${DEV_CHAIN_ID.toString(16)}`,
                  chainName: 'Hardhat Local',
                  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
                  rpcUrls: ['http://127.0.0.1:8545'],
                }
              : {
                  chainId: `0x${PROD_CHAIN_ID.toString(16)}`,
                  chainName: 'BNB Smart Chain',
                  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
                  rpcUrls: ['https://bsc-dataseed.binance.org/'],
                  blockExplorerUrls: ['https://bscscan.com/'],
                }

            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [networkParams],
            })
          } catch (addError) {
            console.error('[NetworkGuard] Failed to add network:', addError)
          }
        } else {
          console.error('[NetworkGuard] Failed to switch network:', error)
        }
      }
    }

    requestSwitch()
  }, [location.pathname, isConnected, chainId])

  // This component doesn't render anything - it just handles the switch request
  return null
}
